import { computed, type Ref } from 'vue';

import BigNumber from '@exchange/helpers/bignumber';
import { balanceService } from '@exchange/libs/balances/service/src';
import { marginService } from '@exchange/libs/futures/service/src';
import type { MarketModel } from '@exchange/libs/market/service/src';
import { MarketType } from '@exchange/libs/market/service/src/lib/market-model';
import { ordersService } from '@exchange/libs/order/my-orders/service/src';
import { orderformService } from '@exchange/libs/order/order-form/service/src';
import { OrderSide, OrderType } from '@exchange/libs/order/shared-model/src/lib/order-essentials';
import { logger } from '@exchange/libs/utils/simple-logger/src';

const useOrderFormComputedHelpers = (market: Ref<MarketModel>, maxLeverage?: Ref<number | undefined>) => {
  const priceMap = computed(() => {
    const { lastPrice } = market.value;

    return {
      LIMIT: orderformService.data.limitPrice,
      STOP: orderformService.data.limitPrice,
      MARKET: lastPrice !== undefined ? new BigNumber(lastPrice) : null,
    };
  });

  const feeForCurrentSide = computed(() => {
    const { base, quote, type } = market.value;
    const price = priceMap.value[orderformService.data.type] || new BigNumber(0);
    const isPerpMarket = type === MarketType.PERP;

    let feeValue: BigNumber = new BigNumber(0);

    // calculate the fee if we got an amount and price
    if (orderformService.data.amount && price) {
      feeValue = new BigNumber(orderformService.feeValue.value || 0).div(100).times(orderformService.data.amount);

      // For perp markets or SELL orders, multiply by price to get quote currency amount
      if (isPerpMarket || orderformService.data.side === OrderSide.SELL) {
        feeValue = feeValue.times(price);
      }
    }

    // For perp markets, always use quote currency
    let sidePrecision: number;
    let currencyCode: string;

    if (isPerpMarket || orderformService.data.side === OrderSide.SELL) {
      sidePrecision = quote.precision;
      currencyCode = quote.code;
    } else {
      sidePrecision = base.precision;
      currencyCode = base.code;
    }

    return {
      value: feeValue,
      code: currencyCode,
      precision: sidePrecision,
    };
  });

  const amountToReceiveForCurrentSide = computed(() => {
    const { base, quote, type } = market.value;
    const { value: currentSideFee } = feeForCurrentSide.value;
    const isPerpMarket = type === MarketType.PERP;

    if (!base || !currentSideFee || !quote) {
      logger.warn('Fees data was requested, but there is no selected market or fee');

      return {
        value: new BigNumber(0),
        code: '-',
        precision: 0,
      };
    }

    const includedFees = currentSideFee;
    let value = new BigNumber(0);

    if (orderformService.data.amount) {
      const price = priceMap.value[orderformService.data.type] || new BigNumber(0);

      if (isPerpMarket || orderformService.data.side === OrderSide.SELL) {
        // For perp markets or SELL orders, calculate in quote currency
        value = new BigNumber(orderformService.data.amount).times(price).minus(includedFees);
      } else {
        // For spot BUY orders, calculate in base currency
        value = new BigNumber(orderformService.data.amount).minus(includedFees);
      }
    }

    // For perp markets, always use quote currency
    let { code, precision } = quote;

    if (!isPerpMarket) {
      if (orderformService.data.side === OrderSide.BUY) {
        code = base.code;
        precision = base.precision;
      }
    }

    return {
      value,
      code,
      precision,
    };
  });

  const availableMaxAmountForPerps = computed(() => {
    const { balance: marginBalance } = marginService;
    const { side } = orderformService.data;

    if (!maxLeverage?.value) {
      return new BigNumber(0);
    }

    const maxAmountWithLeverage = new BigNumber(marginBalance.value.available).times(maxLeverage.value);
    const currentPosition = ordersService.reactivePerpsData.value[market.value.id];

    if (!currentPosition) {
      return maxAmountWithLeverage;
    }

    // If we have a current position and it's being reversed, add the position reversal amount to the max amount
    if ((side === OrderSide.SELL && currentPosition.direction === 'LONG') || (side === OrderSide.BUY && currentPosition.direction === 'SHORT')) {
      /*
       *   Calculate position reversal amount - the amount that can be used to flip from current
       *   position to the opposite direction (x2 because it covers both closing existing position
       *   and opening an equivalent opposing position)
       */
      const positionReversalAmount = new BigNumber(currentPosition.open_position).times(2);

      return maxAmountWithLeverage.plus(positionReversalAmount);
    }

    return maxAmountWithLeverage;
  });

  const availableMaxAmountForCurrentSide = computed(() => {
    const balances = balanceService.balances.value;
    const { base, quote, type } = market.value;
    const isPerpMarket = type === MarketType.PERP;
    const { side } = orderformService.data;

    if (!balances) {
      return new BigNumber(0);
    }

    if (isPerpMarket || side === OrderSide.BUY) {
      let effectiveQuoteBalance = new BigNumber(0);

      // For perp markets, use calculated max amount (margin balance with leverage and current position taken into account)
      if (isPerpMarket) {
        effectiveQuoteBalance = availableMaxAmountForPerps.value;
      } else {
        // For spot market BUY orders, use quote currency
        const balanceQuoteCode = balances[quote.code];

        if (!balanceQuoteCode?.available) {
          return new BigNumber(0);
        }

        effectiveQuoteBalance = new BigNumber(balanceQuoteCode.available);
      }

      if (orderformService.data.limitPrice) {
        return effectiveQuoteBalance.div(orderformService.data.limitPrice);
      }

      const price = priceMap.value[orderformService.data.type];

      if (price) {
        return effectiveQuoteBalance.div(price);
      }

      return new BigNumber(0);
    }

    // For spot SELL orders, always use base currency
    const balanceBaseCode = balances[base.code];

    if (!balanceBaseCode?.available) {
      return new BigNumber(0);
    }

    return new BigNumber(balanceBaseCode.available);
  });

  const balanceBySide = computed(() => {
    const { base, quote, type } = market.value;
    const balances = balanceService.balances.value;
    const { balance: marginBalance } = marginService;
    const isPerpMarket = type === MarketType.PERP;

    let code: string;
    if (isPerpMarket) {
      code = quote.code;
    } else if (orderformService.data.side === OrderSide.BUY) {
      code = quote.code;
    } else {
      code = base.code;
    }

    let precision: number;
    if (isPerpMarket) {
      precision = quote.precision;
    } else if (orderformService.data.side === OrderSide.BUY) {
      precision = quote.precision;
    } else {
      precision = base.precision;
    }

    // For perp markets, always use quote currency balance for margin calculations
    const quoteBalance = balances[quote.code] ?? { available: 0, locked: 0 };
    const sideBalance = isPerpMarket ? quoteBalance : (balances[code] ?? { available: 0, locked: 0 });

    const { available, locked } = sideBalance;
    const availableBN = available ? new BigNumber(available) : new BigNumber(0);
    const lockedBN = locked ? new BigNumber(locked) : new BigNumber(0);

    const result = {
      code,
      precision,
      available: availableBN,
      locked: lockedBN,
    };

    if (isPerpMarket) {
      return {
        ...result,
        marginAvailable: new BigNumber(marginBalance.value.available),
      };
    }

    return result;
  });

  const isMaxAmountAvailable = computed(() => {
    const { amountPrecision } = market.value;
    const smallestBaseValue = new BigNumber(10).pow(-amountPrecision);

    const maxAmountAvailable =
      !!availableMaxAmountForCurrentSide.value &&
      (availableMaxAmountForCurrentSide.value.gt(smallestBaseValue) || availableMaxAmountForCurrentSide.value.isEqualTo(smallestBaseValue));

    if (orderformService.data.type === OrderType.LIMIT || orderformService.data.type === OrderType.STOP) {
      return !!maxAmountAvailable && !!orderformService.data.limitPrice && new BigNumber(orderformService.data.limitPrice).gt(0);
    }

    if (orderformService.data.type === OrderType.MARKET && orderformService.data.side === OrderSide.SELL) {
      return !!maxAmountAvailable;
    }

    return false;
  });

  return {
    feeForCurrentSide,
    amountToReceiveForCurrentSide,
    availableMaxAmountForCurrentSide,
    availableMaxAmountForPerps,
    balanceBySide,
    isMaxAmountAvailable,
  };
};

export default useOrderFormComputedHelpers;
